import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { ChevronLeftIcon, ChevronRightIcon } from '@root/styles/icons';
import { DayPickerRangeController, SingleDatePicker } from 'react-dates';
import { useTranslations } from '@lang';
import { gridTheme } from '@utils/grid';
import { useIsWindowWiderThan } from '@common/hooks';
import { Messages } from '../Messages';
import { DateRangeInputs } from '../DateRangeInputs';
import {
  DesktopLabel,
  DesktopPickerWrapper,
  DateRangePickerWrapper,
  CalendarInfoWrapper,
} from './DesktopInput.styles';
import { DATE_FORMAT } from '../constants';
import { ResetDate } from '../ResetDate';
import { useOnClickOutside } from 'shared-utils';
import { DateType } from '../DateRangeInput';

const MIN_STAY_DEFAULT = 1;

export const DesktopInput = ({
  isSingle,
  startDate,
  endDate,
  handleOnClose,
  extendedDay,
  dayIsUnavailable,
  dayIsHighlighted,
  getAvailability,
  currentSelectedDate,
  showMessage,
  setShowMessage,
  showNoAvailabilityMessage,
  dateType,
  setDateType,
  handleDateChange,
  handleDatesChange,
}) => {
  const { t } = useTranslations();
  const isWiderViewport = useIsWindowWiderThan(gridTheme.breakpoints.md);
  const [inputStartDate, setInputStartDate] = useState(moment(startDate));
  const [inputEndDate, setInputEndDate] = useState(moment(endDate));
  const [keyProp, setKeyProp] = useState(moment(startDate).format('dd-mm-yyyy'));
  const modalRef = useRef<HTMLDivElement>(null);

  const handleUpdateDate = (dates) => {
    setInputStartDate(dates.startDate);
    setInputEndDate(dates?.endDate || null);
    handleDatesChange(dates);
  };

  useEffect(() => {
    if (!endDate || moment(endDate).isSameOrBefore(moment(startDate))) {
      setInputEndDate(
        moment(startDate).add(currentSelectedDate?.minimumStay ?? MIN_STAY_DEFAULT, 'day')
      );
    }
  }, [inputStartDate]);

  const handleResetDate = (dates) => {
    handleUpdateDate(dates);
    setDateType(DateType.STARTDATE);
    setKeyProp(dates.startDate);
  };

  useOnClickOutside(modalRef, () => setDateType(DateType.NONE));

  return (
    <DesktopPickerWrapper>
      <DesktopLabel>{t(isSingle ? 'common.date' : 'common.dates')}</DesktopLabel>
      {isSingle ? (
        <SingleDatePicker
          hideKeyboardShortcutsPanel
          date={moment(startDate)}
          id="your_unique_start_date_id"
          onDateChange={handleDateChange}
          focused={dateType}
          onFocusChange={({ focused }) => setDateType(focused)}
          displayFormat={DATE_FORMAT}
          navNext={<ChevronRightIcon />}
          navPrev={<ChevronLeftIcon />}
        />
      ) : (
        <>
          <DateRangeInputs
            endDate={endDate}
            onClick={() => {
              if (dateType) return setDateType('');
              else
                return handleResetDate({
                  startDate: inputStartDate ?? moment(),
                  endDate: inputEndDate ?? null,
                });
            }}
            inputStartDate={inputStartDate}
          />
          {dateType ? (
            <DateRangePickerWrapper ref={modalRef}>
              <DayPickerRangeController
                key={keyProp}
                disabled={!dateType}
                startDate={inputStartDate}
                endDate={inputEndDate}
                onDatesChange={handleUpdateDate}
                focusedInput={dateType}
                onFocusChange={(fi) => {
                  getAvailability(startDate);
                  setDateType(fi);
                }}
                onClose={handleOnClose}
                renderDayContents={extendedDay}
                isDayBlocked={dayIsUnavailable}
                isDayHighlighted={dayIsHighlighted}
                onNextMonthClick={getAvailability}
                onPrevMonthClick={getAvailability}
                renderCalendarInfo={() => (
                  <CalendarInfoWrapper>
                    <Messages
                      currentSelectedDate={currentSelectedDate}
                      showMessage={showMessage}
                      setShowMessage={setShowMessage}
                      showNoAvailabilityMessage={showNoAvailabilityMessage}
                    />
                    <ResetDate handleResetDate={handleResetDate} />
                  </CalendarInfoWrapper>
                )}
                calendarInfoPosition={showNoAvailabilityMessage ? 'top' : 'bottom'}
                hideKeyboardShortcutsPanel
                navNext={<ChevronRightIcon />}
                navPrev={<ChevronLeftIcon />}
                daySize={50}
                numberOfMonths={isWiderViewport ? 2 : 1}
                initialVisibleMonth={null}
                minDate={moment()}
                minimumNights={currentSelectedDate?.minimumStay || MIN_STAY_DEFAULT}
              />
            </DateRangePickerWrapper>
          ) : null}
        </>
      )}
    </DesktopPickerWrapper>
  );
};
